<template>
  <div
    v-if="userStatus === 'loading'"
    class="loader-container">
    <app-loader />
  </div>
  <div v-else-if="userStatus !== 'loading'" class="settings-form-container">
    <h4 class="settings-form-container__title">
      {{ $t('CP.settings') }}
    </h4>
    <h6 class="user-phone">{{ this.userPhone }}</h6>
    <div class="image-and-form">
      <image-upload
        @loadImage="loadImage"
        :value="imageUrl"
        size="43px"
        isAvatar
      />
      <form @submit.prevent="submitSettings" class="settings-form">
        <app-input-small
          v-model="firstName"
          class="settings-form__input"
          :label="$t('CP.name')"
          :default-text="userNames.firstName"
        />
        <app-input-small
          v-model="lastName"
          class="settings-form__input"
          :label="$t('CP.surname')"
          :default-text="userNames.lastName"
        />
        <div class="settings-form__input">
          <span class="app-input-label">
            {{ $t('CP.gender') }}
          </span>
          <div class="settings-form__input_gender">
            <app-radio-button-small
            class="gender-radio"
            @radioChange="(val) => gender = val"
            name="gender"
            :message="$t('CP.male')"
            value="M"
            :active="gender === 'M'"
            />
            <app-radio-button-small
            class="gender-radio"
            @radioChange="(val) => gender = val"
            name="gender"
            :message="$t('CP.female')"
            value="F"
            :active="gender === 'F'"
            />
          </div>
        </div>
        <div class="settings-form__input">
          <app-select-small
            @changeSelect="(val) => currency = val"
            :indexes="currencySelectInputs.indexes"
            :keys="currencySelectInputs.keys"
            :values="currencySelectInputs.values"
            :default="user.currency"
            searchable
          />
        </div>
      </form>
    </div>
    <form class="password-change" @submit.prevent="changePassword">
      <div class="password-change__title">
        {{ $t('CP.changePassword') }}:
      </div>
      <input-app
        v-model="oldPassword"
        :error="$v.oldPassword.$error"
        type="password"
        :use-lang-hint="true"
        class="password-change__input"
        autocomplete="new-password"
        :placeholder="$t('CP.oldPassword')"
      />
      <div v-if="$v.oldPassword.$dirty && $v.oldPassword.$error"
        class="invalid-message">
        <span v-if="!$v.oldPassword.required">
          {{ $t('validateText.required') }}
        </span>
      </div>
      <input-app
        v-model="newPassword"
        :error="$v.newPassword.$error"
        type="password"
        :use-lang-hint="true"
        class="password-change__input"
        autocomplete="new-password"
        :placeholder="$t('CP.newPassword')"
      />
      <div v-if="$v.newPassword.$dirty && $v.newPassword.$error"
        class="invalid-message">
        <span v-if="!$v.newPassword.required">
          {{ $t('validateText.required') }}
        </span>
        <span v-if="!$v.newPassword.minLength">
          {{ $t('validateText.shortPassword') }}
        </span>
      </div>
      <button-app
        @clicked="changePassword"
        class="password-change__change-button"
        :disabled="disableChangePassword || !oldPassword.length || !newPassword.length"
        color-scheme="gray">
        {{ $t('CP.changePassword') }}
      </button-app>
      <transition name="form__error__fade">
        <p v-if="passwordError" class="form__error">
          {{ $t('validateText.errorChangePasswordSubmit') }}
        </p>
      </transition>
    </form>
<!--    <user-photo-list-->
<!--      @changeParticipants="showFamilyParticipants"-->
<!--      @linkClick="$bvModal.hide('settings')"-->
<!--      :add-link-to-users="true"-->
<!--      :label="$t('CP.myFamily')"-->
<!--      :users="familyMembers"-->
<!--    />-->
    <div class="participants-form-container">
      <h6 class="participants-form-title">
        {{ $t('CP.myFamily') }}:
      </h6>
      <family-participants-form
        class="participants-form"
        @familyChangeStatus="(status) => familyChangeStatus = status"
        title=""
      />
    </div>
    <div>
      <span
        v-if="imageErrorMessage"
        class="form__error-message">
        {{ imageErrorMessage }}
      </span>
    </div>
    <div class="form-buttons">
      <button-app
        class="form-button form-button_cancel"
        @clicked="$bvModal.hide('settings')"
        color-scheme="gray"
        type="button">
        {{ $t('CP.close') }}
      </button-app>
      <button-app
        class="form-button form-button_save"
        color-scheme="blue"
        @clicked="submitSettings"
        type="button"
        :disabled="isDisabled"
      >
        {{ $t('CP.save') }}
      </button-app>
    </div>
    <div class="form-buttons">
      <button-app
        class="form-button form-button_delete"
        @clicked="confirmDeleteAccount"
        color-scheme="gray"
        type="button">
        {{ $t('CP.deleteAccount') }}
      </button-app>
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase,no-unused-vars */
import { parsePhoneNumber } from 'libphonenumber-js';
import { mapState } from 'vuex';
import { minLength, required } from 'vuelidate/lib/validators';
import {
  DELETE_USER_SELECTED_CONTACTS,
  SET_USER_SELECTED_CONTACTS,
  UPDATE_USER_REQUEST,
  CHANGE_PASSWORD_REQUEST,
  SET_EVENT_CONTACTS,
  DELETE_ACCOUNT_REQUEST,
} from '@/store/user/types';
import {
  AUTH_LOGOUT,
} from '@/store/auth/types';
import {
  CLEAR_CURRENT_EVENT, EVENT_PAYOUTS_REQUEST, FETCH_EVENT_EXPENSES_REQUEST,
  FETCH_EVENT_REQUEST, FETCH_EVENT_SUCCESS,
  FETCH_EVENTS_REQUEST, FETCH_OPERATIONS_REQUEST,
  SET_CURRENT_EVENT_ID, USER_PAYOUTS_REQUEST,
} from '@/store/events/types';
import { SET_CONFIRM_MODAL_INFO, SET_CROPPER_INFO } from '@/store/operations/types';
import ImageUpload from '../../components/ImageUpload/ImageUpload';
import AppInputSmall from '../../components/AppInputSmall/AppInputSmall';
import InputApp from '../../components/Input/InputApp';
import ButtonApp from '../../components/Button/ButtonApp';
import AppRadioButtonSmall from '../../components/AppRadioButtonSmall/AppRadioButtonSmall';
import AppSelectSmall from '../../components/AppSelectSmall/AppSelectSmall';
import AppLoader from '../../components/AppLoader/AppLoader';
import FamilyParticipantsForm from '../FamilyParticipantsForm/FamilyParticipantsForm';

export default {
  name: 'SettingsForm',
  components: {
    FamilyParticipantsForm,
    AppLoader,
    AppSelectSmall,
    AppRadioButtonSmall,
    ButtonApp,
    AppInputSmall,
    ImageUpload,
    InputApp,
  },
  validations: {
    newPassword: {
      required,
      minLength: minLength(5),
    },
    oldPassword: {
      required,
    },
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      imageUrl: '',
      gender: '',
      currency: '',
      familyChangeStatus: false,
      newPassword: '',
      oldPassword: '',
      attempt: 0,
      showChangePassword: true,
      disableChangePassword: false,
      passwordSubmitted: false,
      passwordError: false,
      isImageUrlChanged: false,
      imageErrorMessage: '',
    };
  },
  watch: {
    cropperInfo(next) {
      if (next?.destination) {
        this.imageUrl = next.destination;
        this.isImageUrlChanged = true;
      }
    },
  },
  mounted() {
    this.gender = this.user.gender;
    this.currency = this.user.currency;
    this.firstName = this.userNames.firstName;
    this.lastName = this.userNames.lastName;
    this.imageUrl = this.user.avatar_url;
    this.showFamilyParticipants();
  },
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      currencies: (state) => state.currency.currencies,
      userStatus: (state) => state.user.status,
      cropperInfo: (state) => state.operations.cropperInfo,
      events: (state) => state.events.events,
      userSelectedContacts: (state) => state.user.userSelectedContacts,
      family: (state) => {
        try {
          return state.user.user.family;
        } catch (e) {
          return {};
        }
      },
    }),
    userPhone() {
      const parsedPhone = parsePhoneNumber(`+${this.user.phone}`);
      return parsedPhone.formatInternational();
    },
    familyMembers() {
      const { user } = this;
      const family = [];
      if (user.family?.members && user.family?.head) {
        if (user.family.head.uid === user.user) {
          family.push({
            ...user.family.head,
            full_name: this.$t('CP.me'),
          });
        } else {
          family.push(user.family.head);
        }
        user.family.members.forEach((member) => {
          if (member.uid === user.user) {
            family.push({ ...member, full_name: this.$t('CP.me') });
          } else {
            family.push(member);
          }
        });
      }
      return family;
    },
    currencySelectInputs() {
      const indexes = [];
      const keys = [];
      const values = [];
      this.currencies.forEach((currency) => {
        indexes.push(currency.id);
        keys.push(currency.currency);
        values.push(currency.currency);
      });
      return { indexes, keys, values };
    },
    userNames() {
      try {
        const names = this.user.full_name.split(' ');
        const firstName = names.shift();
        return { firstName, lastName: names.join(' ') };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('cannot properly read full name: ', e.message);
        return { firstName: '', lastName: '' };
      }
    },
    isDisabled() {
      let onlySelectedContacts = [...this.userSelectedContacts];
      onlySelectedContacts.shift();
      if (this.family?.members?.length > 0) {
        this.family.members.forEach((member) => {
          onlySelectedContacts = onlySelectedContacts.filter((user) => user.uid !== member.uid);
        });
      }
      return (`${this.firstName} ${this.lastName}` === this.user.full_name
        && this.gender === this.user.gender
        && this.currency === this.user.currency
        && !(this.isImageUrlChanged || this.imageUrl === null))
        && !this.familyChangeStatus
        && this.family?.members?.length === this.userSelectedContacts.length - 1
        && onlySelectedContacts.length === 0;
    },
    familySubmitMembers() {
      const { user } = this;
      const family = [];
      if (user?.family?.members && user?.family?.head) {
        if (user.family.head.uid === user.user) {
          family.push({
            ...user.family.head,
            full_name: this.$t('CP.me'),
          });
        } else {
          family.push(user.family.head);
        }
        user.family.members.forEach((member) => {
          if (member.uid === user.user) {
            family.push({
              ...member,
              full_name: this.$t('CP.me'),
            });
          } else {
            family.push(member);
          }
        });
      }
      return family;
    },
  },
  methods: {
    showFamilyParticipants() {
      try {
        const { familyMembers } = this;
        this.$store.commit(SET_USER_SELECTED_CONTACTS, familyMembers);
        // this.$bvModal.show('family-participants');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('error in family participants: ', e.message);
        // this.$bvModal.show('family-participants');
      }
    },
    async submitSettings() {
      if (this.isDisabled) return;
      let full_name = `${this.firstName} ${this.lastName}`.trim();
      if (!full_name) full_name = null;
      let avatar_url = '';
      if (this.imageUrl) {
        // eslint-disable-next-line prefer-destructuring
        avatar_url = this.imageUrl.split('base64,')[1];
      }
      const { gender, currency } = this;
      // //////////////
      // if (this.family
      //   && this.user.user !== this.family.head.uid
      //   && this.familySubmitMembers.length) {
      //   this.$bvModal.hide('family-participants');
      //   return;
      // }
      const members = this.userSelectedContacts
        .filter((member) => member.phone !== this.user.phone)
        .map((member) => member.phone);
      this.$store.commit(DELETE_USER_SELECTED_CONTACTS);
      // /////////////
      await this.$store.dispatch(UPDATE_USER_REQUEST, {
        full_name,
        gender,
        currency,
        avatar_url,
        family: {
          head: this.user.phone,
          members,
        },
      });
      this.isImageUrlChanged = false;
      this.showFamilyParticipants();
      const currentEvent = await this.$store.dispatch(FETCH_EVENTS_REQUEST, this.$route.params.id);
      if (currentEvent && currentEvent.id) {
        this.$store.commit(SET_CURRENT_EVENT_ID, currentEvent.id);
        this.$store.commit(FETCH_EVENT_SUCCESS, currentEvent);
        this.$store.commit(SET_EVENT_CONTACTS, currentEvent.contacts);
        this.$store.dispatch(FETCH_OPERATIONS_REQUEST, currentEvent.id);
        this.$store.dispatch(EVENT_PAYOUTS_REQUEST, {
          id: currentEvent.id,
        });
        this.$store.dispatch(FETCH_EVENT_EXPENSES_REQUEST, {
          id: currentEvent.id,
          utcOffset: this.$moment().utcOffset(),
        });
      } else {
        this.$store.commit(CLEAR_CURRENT_EVENT);
      }
      this.$store.commit(FETCH_OPERATIONS_REQUEST);
      await this.$store.dispatch(USER_PAYOUTS_REQUEST);
    },
    async changePassword() {
      this.$v.newPassword.$touch();
      this.$v.oldPassword.$touch();
      if (this.$v.newPassword.$anyError || this.$v.oldPassword.$anyError) {
        return null;
      }
      if (this.passwordSubmitted) return;
      this.passwordSubmitted = true;
      this.passwordError = false;
      try {
        const { oldPassword, newPassword } = this;
        await this.$store.dispatch(CHANGE_PASSWORD_REQUEST, {
          old_password: oldPassword,
          password: newPassword,
        });
        this.passwordSubmitted = false;
        this.oldPassword = '';
        this.newPassword = '';
        this.$v.newPassword.$reset();
        this.$v.oldPassword.$reset();
      } catch (e) {
        this.passwordError = true;
        this.showChangePassword = true;
        this.disableChangePassword = false;
        this.passwordSubmitted = false;
        // eslint-disable-next-line no-console
        console.log(e.message);
      }
    },
    loadImage(file) {
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          const img = new Image();
          img.src = reader.result.toString();
          img.onload = () => {
            let imageError = false;
            if (img.width < 70 || img.width > 10000
              || img.height < 70 || img.height > 10000 || file.size > 5242880) {
              this.imageErrorMessage = this.$t('CP.errorOutOfBoundsPictureCrop');
              // eslint-disable-next-line no-console
              console.log('image size should be between 70x70 and 10000x10000 px',
                'current size: ', img.width, img.height);
              imageError = true;
            }
            if (imageError) return;
            this.$store.commit(SET_CROPPER_INFO, {
              src: img.src,
            });
            if (file instanceof File) {
              this.$bvModal.show('cropper');
            } else {
              this.imageUrl = img.src;
              this.isImageUrlChanged = true;
            }
          };
        };
        reader.readAsDataURL(file);
        return null;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e.message);
        throw new Error(e.message);
      }
    },
    confirmDeleteAccount() {
      this.$store.commit(SET_CONFIRM_MODAL_INFO, {
        question: this.$t('CP.confirmDeleteAccount'),
        acceptanceTitle: this.$t('CP.cancel'),
        denialTitle: this.$t('CP.yesDelete'),
        cancelHandler: this.deleteAccount.bind(this),
      });
      this.$bvModal.show('app-confirm-modal');
    },
    async deleteAccount() {
      try {
        await this.$store.dispatch(DELETE_ACCOUNT_REQUEST);
        await this.$store.dispatch(AUTH_LOGOUT);
        this.$bvModal.hide('settings');
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  .loader-container {
    color: var(--text-main-color);
    min-height: 350px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .settings-form-container {
    color: var(--text-main-color);
    min-height: 350px;
  }
  .image-and-form {
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    align-items: center;
    padding: 0;
  }
  .settings-form {
    display: flex;
    flex-direction: column;
    width: 96%;
    align-items: center;
  }
  .settings-form__input {
    flex: 1 0 40%;
    margin: 0;
    width: 100%;
  }
  .settings-form__input_gender {
    display: flex;
    align-items: flex-end;
    min-height: 30px;
    width: 100%;
  }
  .gender-radio {
    margin-right: 20px;
  }
  .password-change {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-between;
    margin: 30px 0;
  }
  .password-change__wrapper {
    display: flex;
    width: 100%;
    align-items: center;
  }
  .password-change__title {
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    flex: 1 0 100%;
  }
  .password-change__subtitle {
    flex: 1 0 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: var(--text-seccond-color);
  }
  .password-change__input {
    margin: 1rem 0 0;
    font-size: 14px;
  }
  .password-change__change-button {
    font-size: 14px;
    font-weight: 600;
    margin-top: 1rem;
  }
  .participants-form-container {
    padding: 12px;
    margin: 12px 0;
    border: 1px solid var(--gray-lighten);
    border-radius: 5px;
  }
  .participants-form-title {
    color: var(--text-main-color);
    opacity: 0.6;
    padding: 0 12px;
  }
  .participants-form {
    padding: 0;
  }
  .form-buttons {
    display: flex;
    flex: 1 0 100%;
    margin: 22px 0 0 0;
  }
  .form__error-message {
    margin: 0 auto 0 0;
    font-size: 1rem;
    color: var(--error-red);
    display: flex;
    align-items: flex-end;
  }
  .form-button {
    width: 50%;
    height: 38px;
    margin: 0;
  }

  .form-button_delete {
    width: 100%;
    color: #dc3545;
  }
  .form-button_cancel {
    margin: 0 16px 0 0;
  }
  .app-input-label {
      display: inline-block;
      font-size: 10px;
      opacity: 0.4;
      margin-top: 8px;
      text-align: left;
      transition: .5s, opacity 0s, height .5s;
    }
    .user-phone {
      text-align: center;
      margin-bottom: -16px;
    }
  @media (min-width: $breakpoint-sm) {
    .user-phone {
      text-align: left;
      margin-bottom: 8px;
    }
    .settings-form {
      flex-direction: row;
      flex-wrap: wrap;
    }
    .settings-form__input {
      flex: 1 0 40%;
      margin: 0 24px 0 0;
      width: 90%;
    }
    .settings-form__input_gender {
      justify-content: flex-start;
      margin: 16px 0 0 0;
    }
    .select-currency {
      justify-content: flex-start;
      flex: 1 1 30%;
    }
    .image-and-form {
      flex-direction: row;
      padding: 4px;
    }
    .app-input-label {
      display: none;
    }
  }
</style>
